import { useCallback, useEffect, useState } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Button,
  Snackbar,
  Box,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import MuiAlert from "@mui/material/Alert";
import styles from "./LoginSection.module.css";
import { Link } from "react-router-dom";
import { resetPassword } from "../../pages/ResetPassword/logic/resetPasswordHelper";
import { Stack } from "@mui/system";

const LoginSection = (props: any) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event: any) => event.preventDefault();

  const handleLogin = useCallback(async () => {
    if (!email || !password) {
      setOpenSnackbar(true);
      return;
    }
    await props?.loginNow(email, password);

    if (rememberMe) {
      localStorage.setItem("rememberMe", "true");
      localStorage.setItem("email", email);
      localStorage.setItem("password", password);
    } else {
      localStorage.removeItem("rememberMe");
      localStorage.removeItem("email");
      localStorage.removeItem("password");
    }
  }, [email, password, rememberMe, props]);

  const handleResetPassword = useCallback(async () => {
    setResetLoading(true);
    try {
      const success = await resetPassword(email);
      setResetLoading(false);

      if (success) {
        setResetSuccess(true);
        setEmailSent(true);
        setPassword("");
      }
    } catch (error) {
      setResetLoading(false);
    }
    // eslint-disable-next-line
  }, [email]);

  const handleBackToLogin = () => {
    setResetSuccess(false);
    setEmailSent(false);
    props?.setLoginResponse(null);
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  // Remember Me autofill
  useEffect(() => {
    const rememberMeValue = localStorage.getItem("rememberMe");
    const savedUsername = localStorage.getItem("email");
    const savedPassword = localStorage.getItem("password");
    if (rememberMeValue === "true" && savedUsername && savedPassword) {
      setEmail(savedUsername);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  // Allow pressing Enter
  useEffect(() => {
    const keyDownHandler = (e: any) => {
      if (e.key === "Enter") {
        handleLogin();
      }
    };
    document.addEventListener("keydown", keyDownHandler);
    return () => document.removeEventListener("keydown", keyDownHandler);
  }, [handleLogin]);

  return (
    <section className={styles.loginWindowContant}>
      <div className={styles.logoSection}>
        <img
          className={styles.optivalLogo1Icon}
          alt=""
          src="/optivallogo-1@2x.png"
        />
      </div>

      {props?.loginResponse?.status === 403 ? (
        emailSent ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" mb={4}>
              Email Sent Successfully!
            </Typography>
            <Typography variant="h6">
              Please check your email for instructions to reset your password.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="h4" gutterBottom mb={4}>
              Your current password is no longer valid.
            </Typography>
            <Typography variant="h6">
              Please create a new password to continue
            </Typography>
          </Box>
        )
      ) : (
        <Stack alignItems="left">
          {" "}
          <Typography variant="h6">welcome back</Typography>
          <Typography variant="h4">Login to your account</Typography>
        </Stack>
      )}

      <form className={styles.actionsSection}>
        {props?.loginResponse?.status !== 403 && (
          <div className={styles.textFielsSection}>
            <TextField
              className={styles.textfield}
              color="secondary"
              variant="outlined"
              type="text"
              label="Email"
              placeholder="Enter Your Email"
              size="medium"
              margin="none"
              value={email}
              error={email ? props?.isValidEmail(email) : ""}
              required
              onChange={(e) => setEmail(e.target.value)}
              helperText={email ? props?.isValidEmail(email) : ""}
            />
            <TextField
              className={styles.textfield}
              value={password}
              color="secondary"
              variant="outlined"
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              label="Password"
              size="medium"
              margin="none"
              error={password ? props?.validPassword(password) : ""}
              required
              onChange={(e) => setPassword(e.target.value)}
              helperText={
                "Must be 8 or more characters (English only) and contain at least 1 uppercase letter, 1 number and 1 special character."
              }
            // helperText={props?.validPassword(password)}
            />
          </div>
        )}
        {props?.loginResponse?.status !== 403 && (
          <div className={styles.additionalActions}>
            <FormControlLabel
              label="Remember Me"
              labelPlacement="end"
              control={
                <Checkbox
                  color="secondary"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  size="medium"
                />
              }
            />
            <Link to="/reset-password" className={styles.typography2}>
              <div className={styles.body2}>Forgot Password?</div>
            </Link>
          </div>
        )}

        {/* LOGIN BUTTON with loader */}

        <Stack spacing={2} alignItems="center" width={"100%"}>
          {props?.loginResponse?.status !== 403 ? (
            <Button
              className={styles.button}
              variant="contained"
              color="secondary"
              onClick={handleLogin}
              disabled={props?.isLoginLoading}
              loading={props?.isLoginLoading}
            >
              LOGIN NOW
            </Button>
          ) : !emailSent && (
            <Button
              className={styles.button}
              variant="contained"
              color="secondary"
              onClick={handleResetPassword}
              disabled={props?.isLoginLoading}
              loading={resetLoading}
            >
              Update now
            </Button>
          )}
          {props?.loginResponse?.status === 403 && (
            <Button
              variant="text"
              color="secondary"
              onClick={handleBackToLogin}
              disabled={props?.isLoginLoading}
            >
              Back to Login
            </Button>
          )}
        </Stack>
      </form>

      {/* Error Snackbar (empty fields) */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleCloseSnackbar}
          severity="error"
        >
          Both email and password are required.
        </MuiAlert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={resetSuccess}
        autoHideDuration={6000}
        onClose={() => setResetSuccess(false)}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setResetSuccess(false)}
          severity="success"
          action={
            <Button color="inherit" size="small" onClick={() => setResetSuccess(false)}>
              CLOSE
            </Button>
          }
        >
          Password reset email sent successfully!
        </MuiAlert>
      </Snackbar>
    </section>
  );
};

export default LoginSection;
